import { useEffect, useState } from 'react';
import { useApi } from '../apiHelpers';

/**
 * Phase 1b dashboard companion hook for the parts-related widgets:
 * LowPartsModule and the StatCardsModule battery total. Replaces the
 * deleted usePartsStore by fetching from `/api/dashboard/parts-status`
 * — one endpoint, the widgets read the slice they care about.
 *
 * Polling/refresh is deliberately not built in here. Dashboard mount
 * fetches once; users navigate away and back to refresh. Matches the
 * cadence of every other 1b dashboard widget.
 */
export interface PartsStatusRow {
  id: number;
  sku: string;
  description: string | null;
  // From the catalog_item_names view (migration 068): what the widgets print.
  base_name?: string | null;
  display_name?: string | null;
  variant_detail?: string | null;
  reorder_threshold: number | null;
  family_key: string | null;
  family_label: string | null;
  quantity_on_hand: number;
  /** Open inbound on sent purchase orders, the reorder report's own figure. */
  on_order: number;
  /** The reorder report lists this part. The one answer to "is it short?". */
  is_short: boolean;
}

export interface PartsStatus {
  rows: PartsStatusRow[];
  battery_total: number;
}

export interface UsePartsStatusResult {
  data: PartsStatus | null;
  loading: boolean;
  error: string | null;
}

export const usePartsStatus = (): UsePartsStatusResult => {
  const api = useApi();
  const [data, setData] = useState<PartsStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    api
      .get('/dashboard/parts-status', { silent: true })
      .then((d: PartsStatus) => {
        if (cancelled) return;
        setData(d);
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setError('Unable to load parts status');
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // useApi() returns a fresh object literal each render; including it in
    // the dep array would re-fire the fetch on every parent re-render. The
    // mount-only fetch is intentional — same pattern as other dashboard
    // modules (AirspaceAuthModule, etc.).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { data, loading, error };
};
